import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import {
  ControlledNumberField,
  ControlledTextField,
  ControlledTextarea,
} from "../../../components/form";
import { BranchFormData, branchSchema } from "../../../schemas/forms";
import { Branch } from "../../../types";
import { BranchFormProps } from "../types/branch.types";

export const BranchForm: React.FC<BranchFormProps> = ({
  onSubmit,
  initialValues,
  isLoading = false,
}) => {
  const form = useForm<BranchFormData>({
    resolver: zodResolver(branchSchema),
    defaultValues: {
      name: initialValues?.name || "",
      slug: initialValues?.slug || "",
      arendaTitle: initialValues?.arendaTitle || "",
      podZakazTitle: initialValues?.podZakazTitle || "",
      contactTitle: initialValues?.contactTitle || "",
      description: initialValues?.description || "",
      address: initialValues?.address || "",
      city: initialValues?.city || "",
      state: initialValues?.state || "",
      country: initialValues?.country || "",
      postalCode: initialValues?.postalCode || "",
      phone: initialValues?.phone || "",
      email: initialValues?.email || "",
      avito_link: initialValues?.avito_link || "",
      yandex_link: initialValues?.yandex_link || "",
      telegram_link: initialValues?.telegram_link || "",
      latitude: initialValues?.latitude,
      longitude: initialValues?.longitude,
      isActive:
        initialValues?.isActive !== undefined ? initialValues.isActive : true,
      isMainBranch:
        initialValues?.isMainBranch !== undefined
          ? initialValues.isMainBranch
          : false,
      crmUrl: initialValues?.crmUrl || "",
      crmToken: initialValues?.crmToken || "",
    },
  });

  const handleFormSubmit = async (data: BranchFormData): Promise<void> => {
    // Helper to convert latitude/longitude to number if needed
    const parseCoordinate = (value: any): number | undefined => {
      if (value === undefined || value === "" || value === null)
        return undefined;
      return typeof value === "string" ? parseFloat(value) : value;
    };

    const latitude = parseCoordinate(data.latitude);
    const longitude = parseCoordinate(data.longitude);

    const branchData: Partial<Branch> = {
      name: data.name,
      ...(data.slug && { slug: data.slug }),
      arendaTitle: data.arendaTitle || "",
      podZakazTitle: data.podZakazTitle || "",
      contactTitle: data.contactTitle || "",
      ...(data.description && { description: data.description }),
      ...(data.address && { address: data.address }),
      ...(data.city && { city: data.city }),
      ...(data.state && { state: data.state }),
      ...(data.country && { country: data.country }),
      ...(data.postalCode && { postalCode: data.postalCode }),
      ...(data.phone && { phone: data.phone }),
      ...(data.email && { email: data.email }),
      ...(data.avito_link && { avito_link: data.avito_link }),
      ...(data.yandex_link && { yandex_link: data.yandex_link }),
      ...(data.telegram_link && { telegram_link: data.telegram_link }),
      // Latitude and longitude are optional - only include if provided
      ...(latitude !== undefined && { latitude }),
      ...(longitude !== undefined && { longitude }),
      isActive: data.isActive,
      isMainBranch: data.isMainBranch,
      ...(data.crmUrl && { crmUrl: data.crmUrl }),
      ...(data.crmToken && { crmToken: data.crmToken }),
    };

    await onSubmit(branchData);
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleFormSubmit)}
      className="form-space-y-2"
    >
      <div className="form-section">
        <h3 className="form-section-title">Основная информация</h3>

        <ControlledTextField
          control={form.control}
          name="name"
          label="Название филиала"
          placeholder="Введите название филиала"
          required
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="slug"
          label="Слаг"
          placeholder="Введите слаг (например: almaty)"
          helperText="Слаг используется в ссылке сайта как маршрут (например: /almaty). Поле необязательно, но рекомендуется."
          disabled={isLoading}
        />

      <ControlledTextField
        control={form.control}
        name="arendaTitle"
        label="Заголовок аренды"
        placeholder="Введите заголовок для аренды"
        disabled={isLoading}
      />

      <ControlledTextField
        control={form.control}
        name="podZakazTitle"
        label="Заголовок под заказ"
        placeholder="Введите заголовок для под заказ"
        disabled={isLoading}
      />

      <ControlledTextField
        control={form.control}
        name="contactTitle"
        label="Заголовок контактов"
        placeholder="Введите заголовок для контактов"
        disabled={isLoading}
      />

        <ControlledTextarea
          control={form.control}
          name="description"
          label="Описание"
          placeholder="Введите описание филиала"
          disabled={isLoading}
          rows={4}
        />
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Местоположение</h3>

        <ControlledTextField
          control={form.control}
          name="address"
          label="Адрес"
          placeholder="Введите адрес"
          disabled={isLoading}
        />

        <div className="form-row">
          <ControlledTextField
            control={form.control}
            name="city"
            label="Город"
            placeholder="Город"
            disabled={isLoading}
          />

          <ControlledTextField
            control={form.control}
            name="state"
            label="Регион/Область"
            placeholder="Регион"
            disabled={isLoading}
          />
        </div>

        <div className="form-row">
          <ControlledTextField
            control={form.control}
            name="country"
            label="Страна"
            placeholder="Страна"
            disabled={isLoading}
          />

          <ControlledTextField
            control={form.control}
            name="postalCode"
            label="Почтовый индекс"
            placeholder="123456"
            disabled={isLoading}
          />
        </div>

        <div className="form-row">
          <ControlledNumberField
            control={form.control}
            name="latitude"
            label="Широта"
            placeholder="55.7558"
            disabled={isLoading}
            step={0.000001}
          />

          <ControlledNumberField
            control={form.control}
            name="longitude"
            label="Долгота"
            placeholder="37.6173"
            disabled={isLoading}
            step={0.000001}
          />
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Контактная информация</h3>

        <ControlledTextField
          control={form.control}
          name="phone"
          label="Телефон"
          placeholder="+7 (999) 123-45-67"
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="email"
          label="Email"
          placeholder="branch@example.com"
          type="email"
          disabled={isLoading}
        />
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Соцсети и ссылки</h3>

        <ControlledTextField
          control={form.control}
          name="avito_link"
          label="Ссылка Avito"
          placeholder="https://www.avito.ru/..."
          type="url"
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="yandex_link"
          label="Ссылка Яндекс"
          placeholder="https://yandex.ru/..."
          type="url"
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="telegram_link"
          label="Ссылка Telegram"
          placeholder="https://t.me/..."
          type="url"
          disabled={isLoading}
        />
      </div>

      <div className="form-section">
        <h3 className="form-section-title">CRM интеграция</h3>

        <ControlledTextField
          control={form.control}
          name="crmUrl"
          label="CRM URL"
          placeholder="https://crm.example.com"
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="crmToken"
          label="CRM токен"
          placeholder="Введите токен для доступа к CRM"
          disabled={isLoading}
        />
      </div>

      <div className="form-wrapper__actions">
        <button
          type="submit"
          className="form-button form-button--primary form-button--full"
          disabled={isLoading}
        >
          {isLoading
            ? "Загрузка..."
            : initialValues
            ? "Сохранить изменения"
            : "Добавить филиал"}
        </button>
      </div>
    </form>
  );
};
